import { useMemo, useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useBoard } from "@/hooks/useBoard";
import { Card, Label, MonoMeta } from "@/design/primitives";
import { cn } from "@/lib/utils";
import CalendarBoardPage from "@/screens/calendar/BoardPage";

// One page, two tabs (Jeff 2026-10-21: "Calendar and Schedule seem like the same").
// Tab "Automation" = the standing jobs; tab "Calendar" = the dated-events client
// (/calendar redirects here ?tab=calendar).
//
// Automation redesign (Jeff 10/24: "I don't understand the Automation tab. The
// design could be better"): the flat 207-row wall became a calm page answering
// three questions in order —
//   1. What is the machine doing?        → plain top strip (counts + 24h health)
//   2. What needs MY attention?          → "Needs a look" (only when non-empty)
//   3. What does a normal day look like? → Next 2 hours + purpose lanes, collapsed
// Lanes unfold on tap. No job list renders by default.

const LANE_ORDER = [
  { key: "watch", label: "Watchdogs — checking the brands stay healthy" },
  { key: "loop", label: "Loops — turning input into cards and work" },
  { key: "beat", label: "Beats — feeding Kit's own feeds" },
  { key: "job", label: "Everything else on schedule" },
];

const laneOf = (name) => {
  const n = (name || "").toLowerCase();
  if (/(watch|sentinel|monitor)/.test(n)) return "watch";
  if (/(loop|ingest|sync|digest|brief)/.test(n)) return "loop";
  if (/(beat|feed|gauge|pulse)/.test(n)) return "beat";
  return "job";
};

const cadenceWord = (cad) => {
  const c = cad || "";
  if (c.endsWith("*/* * * *") || c.startsWith("*/")) return "every few minutes";
  if (/^0 \* \* \* \*$/.test(c)) return "hourly";
  if (/^0 \d+ \* \* \*$/.test(c)) return "a few times daily";
  if (c.endsWith("* * *")) return "daily";
  if (c.endsWith("* * 1") || / 1$/.test(c)) return "weekly-ish";
  return "on its own cadence";
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "";
  const m = Math.max(0, Math.round(ms / 60000));
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

export default function SchedulePage({ initialTab = "automation" }) {
  const desktop = useMediaQuery("(min-width: 1024px)");
  const kitstatus = useBoard("kitstatus");
  // Tab is plain component state; the deep-link case (?tab=calendar) comes in
  // through the route layer (ScheduleWithTab in App.js) as initialTab — the page
  // itself stays Router-free so the render smoke can mount it bare.
  const [tab, setTab] = useState(initialTab === "calendar" ? "calendar" : "automation");
  const [openLane, setOpenLane] = useState(null);

  const schedule = useMemo(
    () => (kitstatus.items || []).find((r) => r.slug === "kit-schedule")?.payload,
    [kitstatus.items],
  );
  const capacity = useMemo(
    () => (kitstatus.items || []).find((r) => r.slug === "kit-capacity")?.payload,
    [kitstatus.items],
  );

  // One declaration + stable identity: the header reads this too — it previously
  // lived only inside the lanes memo and the header crashed on it (blank page, 10/21).
  const jobs = useMemo(() => schedule?.jobs || [], [schedule?.jobs]);

  const lanes = useMemo(() => {
    const grouped = {};
    for (const j of jobs) {
      const lane = laneOf(j.name);
      (grouped[lane] = grouped[lane] || []).push(j);
    }
    return LANE_ORDER.filter((l) => grouped[l.key]?.length).map((l) => ({
      ...l,
      jobs: grouped[l.key],
    }));
  }, [jobs]);

  // Attention lane: jobs whose LAST run failed. Data rides kit-schedule.lastRun
  // (kitstatus-beat P2-redesign); older payloads without lastRun degrade to no lane.
  const attention = useMemo(
    () => jobs.filter((j) => j.lastRun && j.lastRun.ok === false),
    [jobs],
  );

  const queued = capacity?.queued2h || [];
  const scripted = jobs.filter((j) => j.scriptOnly).length;
  const thinking = jobs.length - scripted;

  return (
    <div data-testid="schedule-page" className="w-full font-inter text-os-ink">
      <div className={cn(desktop ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6" : "flex flex-col gap-3 px-4 pb-8 pt-4")}>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h1 className="text-[1.5rem] font-semibold tracking-[-0.02em]">Calendar &amp; Schedule</h1>
        </div>

        {/* Tab bar — Calendar = dated events; Automation = the standing jobs. */}
        <div className="flex gap-2" data-testid="schedule-tabs" role="tablist">
          {[
            { key: "calendar", label: "Calendar" },
            { key: "automation", label: "Automation" },
          ].map((t) => (
            <button
              type="button"
              key={t.key}
              onClick={() => setTab(t.key)}
              aria-selected={tab === t.key}
              role="tab"
              data-testid={`schedule-tab-${t.key}`}
              className={cn(
                "rounded-control border px-4 py-1.5 text-[0.875rem]",
                tab === t.key
                  ? "border-os-border bg-os-wash font-medium text-os-ink shadow-[inset_2px_0_0_0_#FFC838]"
                  : "border-transparent text-os-ink-2 hover:bg-os-wash/60",
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "automation" ? (
          <>
            {/* 1) What is the machine doing — the plain answer, one line. */}
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <MonoMeta as="p">
                {jobs.length
                  ? `${jobs.length} standing jobs · ${scripted} scripted, ${thinking} thinking` +
                    (schedule?.runs24 != null
                      ? ` · last 24h: ${schedule.runs24} run${schedule.runs24 === 1 ? "" : "s"}${schedule.fails24 ? `, ${schedule.fails24} failed` : ", all clear"}`
                      : "")
                  : "reaching the runner…"}
              </MonoMeta>
            </div>

            {/* 2) What needs MY attention — renders only when something failed. */}
            {attention.length > 0 && (
              <Card as="section" className="px-5 py-4" data-testid="schedule-attention">
                <Label as="h2">Needs a look — last run failed</Label>
                <ul className="mt-2 flex flex-col gap-1.5">
                  {attention.map((j, i) => (
                    <li key={i} className="flex flex-col gap-0.5 text-[0.875rem]">
                      <span className="flex items-baseline justify-between gap-3">
                        <span className="text-os-ink">{j.name}</span>
                        <span className="shrink-0 font-mono text-[0.6875rem] text-os-ink-3">
                          {timeAgo(j.lastRun?.at)}
                        </span>
                      </span>
                      {j.lastRun?.error && (
                        <span className="text-[0.75rem] text-os-ink-3">{j.lastRun.error}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* 3a) The rhythm — what fires soon. */}
            <Card as="section" className="px-5 py-4" data-testid="schedule-window">
              <Label as="h2">Next 2 hours</Label>
              {queued.length === 0 ? (
                <p className="mt-1 text-[0.875rem] text-os-ink-2">Nothing scheduled — the window is open for new work.</p>
              ) : (
                <ul className="mt-2 flex flex-col gap-1">
                  {queued.map((q, i) => (
                    <li key={i} className="text-[0.875rem] text-os-ink-2">
                      <span className="font-mono text-[0.6875rem] text-os-ink-3">{q.at}</span>{" — "}{q.name}
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {/* 3b) The full family — collapsed by purpose, unfolds on tap. */}
            {lanes.map((lane) => (
              <section key={lane.key} data-testid={`schedule-lane-${lane.key}`} className="flex flex-col gap-2">
                <button type="button" onClick={() => setOpenLane(openLane === lane.key ? null : lane.key)}
                  className="flex items-baseline justify-between gap-3 text-left">
                  <Label as="h2">{lane.label}</Label>
                  <MonoMeta as="span">{openLane === lane.key ? "tap to hide" : `${lane.jobs.length} ${lane.jobs.length === 1 ? "job" : "jobs"}`}</MonoMeta>
                </button>
                {openLane === lane.key && (
                  <Card className="px-5 py-4">
                    <ul className="flex flex-col gap-1.5">
                      {lane.jobs.map((j, i) => (
                        <li key={i} className="flex flex-col gap-0.5 text-[0.875rem]">
                          <span className="flex items-baseline justify-between gap-3">
                            <span className="text-os-ink">{j.name}</span>
                            <span className="shrink-0 font-mono text-[0.6875rem] text-os-ink-3">
                              {cadenceWord(j.cadence)} · {j.scriptOnly ? "scripted" : "thinking"}
                            </span>
                          </span>
                          {j.lastRun && j.lastRun.ok === false && (
                            <span className="text-[0.75rem] text-os-warn">last run failed {timeAgo(j.lastRun?.at)}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </Card>
                )}
              </section>
            ))}

            <MonoMeta as="p">freshest reading: {schedule?.updated ? new Date(schedule.updated).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—"} · Kit remeasures every 10 minutes</MonoMeta>
          </>
        ) : (
          /* Calendar tab: the same three-pane client the old /calendar page rendered,
             embedded without its page container (CalendarBoardPage `bare` mode).
             The bare embed owns its own data fetch (useBoard("calendar") lives inside
             CalendarBoardPage regardless of mode) — the Schedule page only owns tabs. */
          <CalendarBoardPage bare />
        )}
      </div>
    </div>
  );
}